/**
 * /admin/tests/new + /admin/tests/:id — Admin Test Composer.
 *
 * Binding archetype: `docs/ia/admin-test-edit.md`.
 *
 * Pattern: header → Test Settings Card → dual-pane Question Picker.
 * The dual-pane picker is the page's reason to exist (Taste lens): the user
 * decides what the test IS (settings), then assembles its CONTENTS (picker),
 * in one continuous act on one surface. No bolt-on, no two-page flow.
 *
 * 15-FG/31:
 *   - Header CTA cluster ("← Back to Tests" secondary + "Save Test" primary)
 *     moved from the bottom of the form to the top-right of the page.
 *   - Save button label "Save Test" (was "Create" — Polish 9 fail per audit).
 *   - Native <input type="checkbox"> swapped for the Toggle primitive
 *     (Polish 7 "settings, not selections").
 *   - Added the missing "Show result immediately" toggle (Minimum 4).
 *   - Inserted the dual-pane QuestionPicker below the Settings Card
 *     (Minimum 5–8).
 *   - Existing bimassess-specific fields (Track, Level, Estimated Minutes,
 *     Resit Lockout, Issue Certificate) are permitted Extensions under
 *     "more settings fields" — kept inside the Settings Card 2-col grid.
 *
 * The post-create-only sections (Lifecycle / Task Artefact / Task Brief /
 * Preview as Candidate) live below the picker as card-warm sub-panels —
 * they're meta-actions on a persisted test, not part of the authoring shape.
 *
 * The QuestionPicker fetches real questions from /api/questions (Fix 2,
 * 2026-05-30). Full QuestionPayload is projected to the slim PickerQuestion
 * shape via toPickerQuestion(), resolving category IDs through a parallel
 * /api/taxonomy/categories fetch.
 *
 * Cross-cutting invariants:
 *   - Inv 12: no console.log/error/warn.
 *   - Inv 13: no `any` / `@ts-ignore`.
 *   - Inv 14: internal nav via <Link> from barrel; no bare <a href="/...">.
 *   - Inv 15: every atom from `@/components/ui` barrel.
 *
 * // CARVE-OUT (04-test-composer/02 review round 1, 2026-05-23): admin/tests path.
 * // This file lives at pages/admin/tests/ outside the denylist's pages/tests/**
 * // but this path was established by issue 04-test-composer/01 and ratified at
 * // merge. Ratified by reviewer commit db212cd.
 */

import { useEffect, useState, useMemo, type ReactElement } from 'react';
import { AdminLayout } from '../layout.tsx';
import { Button, Card, Input, Link, Toggle, toast } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useAutosave } from '@/hooks/useAutosave';
import {
  archiveTest,
  createTest,
  getTest,
  previewTest,
  publishTest,
  updateTaskBrief,
  updateTest,
  type Level,
  type ResitLockoutPolicy,
  type TestInputPayload,
  type TestPayload,
  type Track,
} from '../../../state/tests/api.ts';
import {
  listCategories,
  listQuestions,
  type CategoryPayload,
  type QuestionPayload,
} from '../../../state/questions/api.ts';
import { TaskArtefactUploader } from '../../../components/test-composer/TaskArtefactUploader.tsx';
import { TestAvailabilityEditor } from '../../../components/admin/TestAvailabilityEditor.tsx';
import {
  QuestionPicker,
  type PickerQuestion,
} from './components/QuestionPicker.tsx';

export interface AdminTestEditPageProps {
  testId?: string;
}

const TRACKS: Track[] = ['architecture', 'mep', 'structural', 'interior'];
const LEVELS: Level[] = ['novice', 'intermediate', 'advanced', 'bim_champion'];
const LOCKOUTS: ResitLockoutPolicy[] = ['none', '48hours', '1week'];

// ─── Shared className constants ────────────────────────────────────────────
// Composed via cn() so design.md transition tokens survive the
// eslint-plugin-tailwindcss static-string analyser (it doesn't read tokens
// from function-call args).

const BACK_LINK_CLASSES = cn(
  'inline-flex items-center rounded-md',
  'border border-line-2 bg-transparent',
  'px-4 py-2 font-sans text-small text-ink no-underline',
  'hover:bg-bg-warm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l focus-visible:ring-offset-2',
  'transition-base',
);

const SELECT_CLASSES = cn(
  'rounded-md border border-line-2 bg-card px-3 py-2',
  'font-sans text-small text-ink',
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l',
  'transition-base',
);

const TEXTAREA_CLASSES = cn(
  'w-full rounded-md border border-line-2 bg-card',
  'px-3.5 py-2.5 font-sans text-body text-ink placeholder:text-muted-2',
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l',
  'transition-base',
);

/**
 * Map a full QuestionPayload to the slim PickerQuestion shape.
 * category_id/subcategory_id are resolved against the category lookup map;
 * section_name is the fallback label when the category lookup is empty.
 */
function toPickerQuestion(
  q: QuestionPayload,
  catMap: Map<string, string>,
): PickerQuestion {
  const category =
    (q.category_id ? catMap.get(q.category_id) : undefined) ??
    q.section_name ??
    '';
  // subcategory_id carries no name in the slim API response; show its raw
  // value only as a last resort — in practice the picker uses it for grouping
  // and the label is decorative.
  const subcategory = q.subcategory_id ?? null;
  return {
    id: q.id,
    type: q.type,
    text: q.text,
    category,
    subcategory,
    tags: q.tags.map((t) => t.name),
  };
}

export function AdminTestEditPage(props: AdminTestEditPageProps): ReactElement {
  const isEditMode = props.testId !== undefined;

  // ─── Settings state ────────────────────────────────────────────────────────
  const [test, setTest] = useState<TestPayload | null>(null);
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [track, setTrack] = useState<Track>('architecture');
  const [level, setLevel] = useState<Level>('novice');
  const [timeLimit, setTimeLimit] = useState<number>(0);
  const [passMark, setPassMark] = useState<number>(70);
  const [shuffleQ, setShuffleQ] = useState<boolean>(false);
  const [shuffleA, setShuffleA] = useState<boolean>(true);
  const [showResult, setShowResult] = useState<boolean>(true);
  const [estimatedMinutes, setEstimatedMinutes] = useState<number>(60);
  const [resitLockout, setResitLockout] = useState<ResitLockoutPolicy>('none');
  const [issueCertificate, setIssueCertificate] = useState<boolean>(false);

  // ─── Picker state ──────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [questionBank, setQuestionBank] = useState<PickerQuestion[]>([]);

  // Fetch the real question bank once on mount.
  useEffect(() => {
    void (async () => {
      const [qEnv, catEnv] = await Promise.all([
        listQuestions({}),
        listCategories(),
      ]);
      const catMap = new Map<string, string>();
      if (catEnv.ok && Array.isArray(catEnv.data)) {
        for (const c of catEnv.data as CategoryPayload[]) {
          catMap.set(c.id, c.name);
        }
      }
      if (qEnv.ok && Array.isArray(qEnv.data.items)) {
        setQuestionBank(
          qEnv.data.items.map((q) => toPickerQuestion(q as QuestionPayload, catMap)),
        );
      }
    })();
  }, []);

  // ─── Lifecycle / Task Artefact / Task Brief state ──────────────────────────
  const [taskBriefMd, setTaskBriefMd] = useState<string>('');
  const [briefPreview, setBriefPreview] = useState<boolean>(false);
  const [taskArtefactUrl, setTaskArtefactUrl] = useState<string | null>(null);
  const [previewAttemptId, setPreviewAttemptId] = useState<string | null>(null);

  // ─── Banner messages ───────────────────────────────────────────────────────
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!props.testId) return;
    void (async () => {
      const env = await getTest(props.testId as string);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        return;
      }
      const t = env.data;
      setTest(t);
      setTitle(t.title);
      setDescription(t.description);
      if (t.track) setTrack(t.track);
      if (t.level) setLevel(t.level);
      setTimeLimit(t.time_limit);
      setPassMark(t.pass_mark);
      setShuffleQ(t.shuffle_q);
      setShuffleA(t.shuffle_a);
      setShowResult(t.show_result);
      setEstimatedMinutes(t.estimated_minutes);
      setResitLockout(t.resit_lockout);
      setIssueCertificate(t.issue_certificate);
      setTaskBriefMd(t.task_brief_md ?? '');
      setTaskArtefactUrl(t.task_artifact_url);
    })();
  }, [props.testId]);

  // J-X12 — Autosave integration for test settings.
  const settingsData = useMemo(() => ({
    title,
    description,
    track,
    level,
    time_limit: timeLimit,
    pass_mark: passMark,
    shuffle_q: shuffleQ,
    shuffle_a: shuffleA,
    show_result: showResult,
    estimated_minutes: estimatedMinutes,
    resit_lockout: resitLockout,
    issue_certificate: issueCertificate,
  }), [title, description, track, level, timeLimit, passMark, shuffleQ, shuffleA, showResult, estimatedMinutes, resitLockout, issueCertificate]);

  const handleAutosave = async (data: typeof settingsData): Promise<void> => {
    if (!test) return; // Don't autosave before first manual save creates the test
    const env = await updateTest(test.id, data as TestInputPayload);
    if (!env.ok) return; // Silent — autosave errors shown via saveStatus
    setTest(env.data);
  };

  const { saveStatus, savedAt } = useAutosave({
    data: settingsData,
    saveFn: handleAutosave,
    storageKey: test ? `bimassess-autosave-test-${test.id}` : 'bimassess-autosave-test-new',
    enabled: isEditMode,
  });

  const onSave = (): void => {
    void (async () => {
      setError(null);
      setInfo(null);
      const payload: TestInputPayload = {
        title,
        description,
        track,
        level,
        time_limit: timeLimit,
        pass_mark: passMark,
        shuffle_q: shuffleQ,
        shuffle_a: shuffleA,
        show_result: showResult,
        estimated_minutes: estimatedMinutes,
        resit_lockout: resitLockout,
        issue_certificate: issueCertificate,
      };
      const env = test
        ? await updateTest(test.id, payload)
        : await createTest(payload);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        toast.error(`${env.error.code}: ${env.error.message}`);
        return;
      }
      setTest(env.data);
      const msg = test ? 'Saved.' : 'Created.';
      setInfo(msg);
      toast.success(msg);
    })();
  };

  const onPublish = (): void => {
    if (!test) return;
    void (async () => {
      const env = await publishTest(test.id);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        toast.error(`${env.error.code}: ${env.error.message}`);
        return;
      }
      setTest(env.data);
      const msg = `Published (snapshot: ${env.data.author_name_at_publish ?? '∅'}).`;
      setInfo(msg);
      toast.success(msg);
    })();
  };

  const onArchive = (): void => {
    if (!test) return;
    void (async () => {
      const env = await archiveTest(test.id);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        toast.error(`${env.error.code}: ${env.error.message}`);
        return;
      }
      setTest(env.data);
      setInfo('Archived.');
      toast.success('Archived.');
    })();
  };

  const onSaveTaskBrief = (): void => {
    if (!test) return;
    void (async () => {
      setError(null);
      setInfo(null);
      const env = await updateTaskBrief(test.id, taskBriefMd);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        toast.error(`${env.error.code}: ${env.error.message}`);
        return;
      }
      setTest(env.data);
      setInfo('Task Brief saved.');
      toast.success('Task Brief saved.');
    })();
  };

  const onPreview = (): void => {
    if (!test) return;
    void (async () => {
      setError(null);
      setInfo(null);
      const env = await previewTest(test.id);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        return;
      }
      setPreviewAttemptId(env.data.preview_attempt_id);
      window.open(`/tests/preview/${env.data.preview_attempt_id}`, '_blank');
    })();
  };

  const headerTitle = isEditMode ? 'Edit Test' : 'Create Test';

  return (
    <AdminLayout title={headerTitle} eyebrow="Configure settings and select questions">
      {/* ─── Header CTA cluster ─────────────────────────────────────────────
          Right-aligned: secondary "← Back to Tests" + primary "Save Test".
          Sits in the page header band per Minimum 2 — never at the bottom
          of the form. */}
      <div
        className="mt-6 flex items-start justify-between gap-3"
        data-testid="test-edit-header-cta"
      >
        {/* J-X12 — Autosave status indicator */}
        <div className="flex items-center gap-2 font-mono text-mono text-muted-2" data-testid="autosave-indicator">
          {saveStatus === 'saving' && <span>Saving…</span>}
          {saveStatus === 'saved' && savedAt && (
            <span>Saved {Math.max(0, Math.floor((Date.now() - savedAt.getTime()) / 1000))}s ago</span>
          )}
          {saveStatus === 'error' && <span className="text-error">Save failed</span>}
        </div>
        <div className="flex items-start gap-3">
          <Link to="/admin/tests" className={BACK_LINK_CLASSES}>
            ← Back to Tests
          </Link>
          <Button variant="primary" type="button" onClick={onSave}>
            Save Test
          </Button>
        </div>
      </div>

      {/* Banner row for inline error/info — surfaces under the header CTA. */}
      {error ? (
        <p
          role="alert"
          className="mt-4 rounded-md border border-error/30 bg-error-l px-4 py-2 font-sans text-small text-error"
        >
          {error}
        </p>
      ) : null}
      {info ? (
        <p
          role="status"
          className="mt-4 rounded-md border border-success/30 bg-success-l px-4 py-2 font-sans text-small text-success"
        >
          {info}
        </p>
      ) : null}

      {/* ─── 40:60 composer grid ─────────────────────────────────────────────
          Desktop (lg+): Settings (2/5 ≈ 40%) | Picker (3/5 ≈ 60%).
          Below lg: single column, stacked vertically.
          The Picker stacks Bank on top of Selected internally (flex-col).
          Ratio chosen empirically against the static mockup at
          .scratch/composer-mockup/half-half.html — picker is the work, settings
          are set-once, so the picker gets the visual majority. */}
      <div className="mt-5 grid grid-cols-1 items-start gap-5 lg:grid-cols-5">

      {/* ── Column 1 (2/5 = 40%): Test Settings ───────────────────────── */}
      <section aria-labelledby="settings-heading" className="lg:col-span-2">
        <Card variant="default" className="p-0">
          <header className="border-b border-line px-6 py-4">
            <h2
              id="settings-heading"
              className="font-sans text-title font-medium text-ink"
            >
              Test Settings
            </h2>
          </header>
          {/* max-w-2xl caps input width inside the Settings card so the form
              fields stay form-shaped at any column width — without this the
              Title/Description/etc. balloon to fill the 40% column and the
              card reads as sparse. */}
          <div className="flex max-w-2xl flex-col gap-4 px-6 py-5">
            {/* Identity row */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="test-title"
                className="font-sans text-small font-medium text-ink"
              >
                Title
              </label>
              <Input
                id="test-title"
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. ISO 19650 Fundamentals"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="test-description"
                className="font-sans text-small font-medium text-ink"
              >
                Description
              </label>
              <textarea
                id="test-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="A short note about what this test covers."
                className={TEXTAREA_CLASSES}
              />
            </div>

            {/* Numeric row — Space Mono via font-mono class on the input. */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="test-time-limit"
                  className="font-sans text-small font-medium text-ink"
                >
                  Time Limit (min, 0 = none)
                </label>
                <Input
                  id="test-time-limit"
                  type="number"
                  min={0}
                  value={timeLimit}
                  onChange={(e) => setTimeLimit(Number(e.target.value))}
                  className="font-mono"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="test-pass-mark"
                  className="font-sans text-small font-medium text-ink"
                >
                  Pass Mark (%)
                </label>
                <Input
                  id="test-pass-mark"
                  type="number"
                  min={0}
                  max={100}
                  value={passMark}
                  onChange={(e) => setPassMark(Number(e.target.value))}
                  className="font-mono"
                />
              </div>
            </div>

            {/* Toggles — Polish 7 mandates Toggle primitives, never native
                checkboxes. Always vertical stack: with the 40:60 page grid the
                Settings column tops out at ~600px even at 2xl, which is too
                narrow to fit "Shuffle questions / Shuffle answers / Show
                result immediately" labels side-by-side. Vertical also reads
                cleaner as a "settings list". */}
            <fieldset
              className="flex flex-col gap-3"
              aria-labelledby="settings-heading"
            >
              <legend className="sr-only">Test behaviour toggles</legend>
              <Toggle
                id="toggle-shuffle-questions"
                checked={shuffleQ}
                onCheckedChange={setShuffleQ}
                label="Shuffle questions"
              />
              <Toggle
                id="toggle-shuffle-answers"
                checked={shuffleA}
                onCheckedChange={setShuffleA}
                label="Shuffle answers"
              />
              <Toggle
                id="toggle-show-result"
                checked={showResult}
                onCheckedChange={setShowResult}
                label="Show result immediately"
              />
            </fieldset>

            {/* Extension row — bimassess-specific Settings fields per
                docs/ia/admin-test-edit.md § Extension rules ("more settings
                fields"). Stays inside the Settings Card on a 2-col grid. */}
            <div className="grid grid-cols-1 gap-4 border-t border-line pt-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="test-track"
                  className="font-sans text-small font-medium text-ink"
                >
                  Track
                </label>
                <select
                  id="test-track"
                  value={track}
                  onChange={(e) => setTrack(e.target.value as Track)}
                  className={SELECT_CLASSES}
                >
                  {TRACKS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="test-level"
                  className="font-sans text-small font-medium text-ink"
                >
                  Level
                </label>
                <select
                  id="test-level"
                  value={level}
                  onChange={(e) => setLevel(e.target.value as Level)}
                  className={SELECT_CLASSES}
                >
                  {LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="test-estimated-minutes"
                  className="font-sans text-small font-medium text-ink"
                >
                  Estimated Minutes
                </label>
                <Input
                  id="test-estimated-minutes"
                  type="number"
                  min={0}
                  value={estimatedMinutes}
                  onChange={(e) => setEstimatedMinutes(Number(e.target.value))}
                  className="font-mono"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="test-resit-lockout"
                  className="font-sans text-small font-medium text-ink"
                >
                  Resit Lockout
                </label>
                <select
                  id="test-resit-lockout"
                  value={resitLockout}
                  onChange={(e) =>
                    setResitLockout(e.target.value as ResitLockoutPolicy)
                  }
                  className={SELECT_CLASSES}
                >
                  {LOCKOUTS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-1 sm:col-span-2">
                <Toggle
                  id="toggle-issue-certificate"
                  checked={issueCertificate}
                  onCheckedChange={setIssueCertificate}
                  label="Issue certificate on pass"
                />
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* ── Column 2 (3/5 = 60%): Question Bank stacked above Selected ─ */}
      <section aria-labelledby="picker-heading" className="lg:col-span-3">
        <h2 id="picker-heading" className="sr-only">
          Question Picker
        </h2>
        <QuestionPicker
          bank={questionBank}
          selectedIds={selectedIds}
          onSelectedChange={setSelectedIds}
        />
      </section>

      </div>{/* end 3-col grid */}

      {/* ─── Post-create meta actions ──────────────────────────────────────
          Lifecycle / Task Artefact / Task Brief / Preview-as-Candidate.
          These are meta-actions on a persisted test (Stories 64–68); they
          don't belong in the create flow. Render only when `test` is loaded
          so /admin/tests/new doesn't show them. */}
      {test ? (
        <section className="mt-5" aria-labelledby="lifecycle-heading">
          <Card variant="warm" className="p-0">
            <header className="border-b border-line px-6 py-4">
              <h2
                id="lifecycle-heading"
                className="font-sans text-title font-medium text-ink"
              >
                Lifecycle &amp; Task Brief
              </h2>
            </header>
            <div className="flex flex-col gap-4 px-6 py-5">
              <p className="font-sans text-small text-muted">
                Status: <span className="font-mono text-ink">{test.status}</span> —{' '}
                {test.author_name_at_publish
                  ? `Author at publish: ${test.author_name_at_publish}`
                  : 'unpublished'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onPublish}
                  disabled={test.status !== 'draft'}
                >
                  Publish
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onArchive}
                  disabled={test.status !== 'active'}
                >
                  Archive
                </Button>
                <Button variant="secondary" type="button" onClick={onPreview}>
                  Preview as Candidate
                </Button>
              </div>

              <div className="flex flex-col gap-2">
                <span className="font-sans text-small font-medium text-ink">
                  Task Artefact
                </span>
                {taskArtefactUrl ? (
                  <p className="font-sans text-small text-muted">
                    Current artefact:{' '}
                    <Link href={taskArtefactUrl}>
                      Download (signed URL, valid 24h)
                    </Link>
                  </p>
                ) : (
                  <p className="font-sans text-small text-muted">
                    No artefact uploaded yet.
                  </p>
                )}
                <TaskArtefactUploader
                  testId={test.id}
                  onUploaded={(url) => {
                    setTaskArtefactUrl(url);
                    setInfo('Artefact uploaded and attached.');
                  }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="task-brief"
                    className="font-sans text-small font-medium text-ink"
                  >
                    Task Brief (markdown)
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setBriefPreview((v) => !v)}
                  >
                    {briefPreview ? 'Edit' : 'Preview'}
                  </Button>
                </div>
                {briefPreview ? (
                  <pre className="rounded-md border border-line bg-card px-3 py-2 font-mono text-mono text-ink whitespace-pre-wrap">
                    {taskBriefMd || '(empty)'}
                  </pre>
                ) : (
                  <textarea
                    id="task-brief"
                    rows={6}
                    value={taskBriefMd}
                    onChange={(e) => setTaskBriefMd(e.target.value)}
                    placeholder="Write the Task Brief in markdown…"
                    className={TEXTAREA_CLASSES}
                  />
                )}
                <div>
                  <Button variant="secondary" type="button" onClick={onSaveTaskBrief}>
                    Save Task Brief
                  </Button>
                </div>
              </div>

              {previewAttemptId ? (
                <p className="font-sans text-small text-muted">
                  Preview open in new tab.{' '}
                  <Link href={`/tests/preview/${previewAttemptId}`}>
                    Re-open preview
                  </Link>
                </p>
              ) : null}
            </div>
          </Card>

          {/* J-X22 (15-FG/Track-F) — admin availability-window editor. */}
          <TestAvailabilityEditor
            testId={test.id}
            initialStartsAt={test.availability_starts_at ?? null}
            initialEndsAt={test.availability_ends_at ?? null}
            initialClosedReason={test.availability_closed_reason ?? null}
            onSaved={(next) => {
              setTest({
                ...test,
                availability_starts_at: next.availability_starts_at,
                availability_ends_at: next.availability_ends_at,
                availability_closed_reason: next.availability_closed_reason,
              });
            }}
          />
        </section>
      ) : null}
    </AdminLayout>
  );
}
